import { useMemo, useCallback, useState } from 'react';
import { isNil } from 'lodash';
import i18n from 'i18n';
import { useFetch } from 'utils/sdk';
import { LanguageEnum, LocalStorageKeyEnum } from 'utils/constants';
import { login as loginRequest, logout as logoutRequest, setUserLanguagePreference } from './sdk';
import { IUserInfo, IAuthContextValue, IAuthProviderProps } from './auth.interfaces';
import { AuthContext } from './AuthContext';

export const AuthProvider = ({ children }: IAuthProviderProps) => {
  const [token, setToken] = useState(localStorage.getItem(LocalStorageKeyEnum.AUTH_TOKEN));

  const {
    data: userInfo,
    isLoading,
    mutate,
  } = useFetch<IUserInfo>(token ? 'api/customer-facing/me/' : null, {
    revalidateIfStale: false,
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
  });

  const login = useCallback(
    async ({ email, password }: { email: string; password: string }) => {
      // Purposely not handling the error here, let the caller handle it
      const response = await loginRequest({ email, password, locale: i18n.language ?? 'en' });
      localStorage.setItem(LocalStorageKeyEnum.AUTH_TOKEN, response.access_token);
      setToken(response.access_token);

      // TODO: Set user details for sentry
      // Sentry.setUser({
      // id: user.id,
      // email: user.email,
      // });
      // Then on logout:
      // Sentry.configureScope(scope => scope.setUser(null));
      // revalidate user

      await mutate();
    },
    [mutate]
  );

  const logout = useCallback(async () => {
    // Purposely not handling the error here, let the caller handle it
    await logoutRequest();

    localStorage.removeItem(LocalStorageKeyEnum.AUTH_TOKEN);
    localStorage.removeItem(LocalStorageKeyEnum.LANGUAGE_PREFERENCE);
    setToken(null);

    // clear SWR cache for this key
    await mutate(undefined, false);
  }, [mutate]);

  // TODO: Make this function more generic to update any user info
  const updateUserLanguage = useCallback(
    async (language: LanguageEnum) => {
      localStorage.setItem(LocalStorageKeyEnum.LANGUAGE_PREFERENCE, language);
      i18n.changeLanguage(language);

      if (!userInfo) {
        return;
      }

      try {
        // Call API to update language
        await setUserLanguagePreference({ language });

        // Revalidate user info
        await mutate();
      } catch (error) {
        console.error('Failed to update language', error);
      }
    },
    [mutate, userInfo]
  );

  const value = useMemo<IAuthContextValue>(
    () => ({
      userInfo: userInfo ?? null,
      isAuthenticated: !isNil(token),
      isLoading,
      login,
      logout,
      updateUserLanguage,
    }),
    [userInfo, token, isLoading, login, logout, updateUserLanguage]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
